// タスクG: 下地地図ベクトルタイル (basemap-v1.pmtiles, gpv-basemap-v1)。
// - 転送のみタイル化し、描画は従来どおり純 SVG (品質不変)。
// - 線はフィーチャ id で断片を連結 (stitching) して1本の連続パスへ戻すため、
//   cap/join/AA が現行と同一で切れ目が構造的に出ない。塗りはタイル矩形で閉じた
//   evenodd リング (境界頂点は量子化格子上で隣接タイルと一致)。
// - 表示域 +1 リングを読むので、連結後パスの真の切断端は常に画面外。
// - クラス名は現行 g id を踏襲し、index.html の <use> (_a=塗り/_f=線) と
//   スタイル UI がそのまま効く。緯経線 (lonlat) は数式生成 (同心円+放射線)。
import { PMTiles } from "pmtiles";
import type { MapView } from "./Basemap";
import { type MapModel, getMapMeta } from "./MapMeta";
import { readNumber } from "./UrlState";
import { dataPath, resourcePath } from "./paths";

// 発注者要件 (2026-07-22): データ範囲 ±12800 の 16:9 表示で左右に見える
// x ±22,756km まで地図を保証 → 格子は ±23,040 (z0〜6)。値は生成側と一致必須
// (activate() で pmtiles metadata と照合し、ずれていれば fail-fast)。
const ORIGIN = -23040;
const SPAN = 46080;
const Q = 0.05;
const UNITS = 921600;
const ZMAX = 6;
// アーカイブ更新時は必ず版番号 (v1.00) を上げて同名差し替えをしない (immutable キャッシュ)。
const ARCHIVE = "basemap/GPVmap-polar_v1.00.pmtiles";

const CLASS_NAMES: Record<number, string> = {
    1: "japan_coast",
    2: "japan_pref-b",
    3: "japan_hokkaido-b",
    4: "foreign_coast",
    5: "foreign_border",
    6: "lake",
};
// 塗りを持つクラス (陸・湖)。線のみのクラスは fill サブグループを作らない。
const SVG_NS = "http://www.w3.org/2000/svg";

interface LinePiece {
    fid: number;
    seq: number; // フィーチャ内の断片順序 (連結は seq 隣接で行う)
    flags: number; // 1=閉リングの断片, 2=完結した閉リング
    pts: Int32Array; // グローバル 0.05km 整数格子 [x0,y0,x1,y1,...]
}
interface DecodedTile {
    fills: Map<number, string[]>; // cid -> path d (タイル内で完結、evenodd)
    lines: Map<number, LinePiece[]>;
}

async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
    const stream = new Blob([bytes as BlobPart])
        .stream()
        .pipeThrough(new DecompressionStream("deflate"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

function km(u: number): string {
    // 0.05km 格子 → km 文字列 (最大2桁小数、末尾ゼロ除去)
    return String(Math.round(u * 5) / 100 + ORIGIN);
}

class Varint {
    pos = 0;
    constructor(private readonly buf: Uint8Array) {}
    read(): number {
        let shift = 0;
        let val = 0;
        for (;;) {
            const b = this.buf[this.pos++];
            val |= (b & 0x7f) << shift;
            if (!(b & 0x80)) {
                return val >>> 0;
            }
            shift += 7;
        }
    }
    zigzag(): number {
        const v = this.read();
        return (v >>> 1) ^ -(v & 1);
    }
    u8(): number {
        return this.buf[this.pos++];
    }
    u16(): number {
        const v = this.buf[this.pos] | (this.buf[this.pos + 1] << 8);
        this.pos += 2;
        return v;
    }
}

function decodeTile(raw: Uint8Array, z: number, ix: number, iy: number): DecodedTile {
    const t = Math.floor(UNITS / (1 << z));
    const ox = ix * t;
    const oy = iy * t;
    const v = new Varint(raw);
    if (v.u8() !== 2) {
        throw new Error("unsupported basemap payload");
    }
    const fills = new Map<number, string[]>();
    const lines = new Map<number, LinePiece[]>();
    const nblocks = v.u8();
    for (let b = 0; b < nblocks; b++) {
        const cid = v.u8();
        const nfill = v.u16();
        const dsFill: string[] = [];
        for (let i = 0; i < nfill; i++) {
            const nrings = v.u8();
            let d = "";
            for (let r = 0; r < nrings; r++) {
                const npts = v.read();
                let px = 0;
                let py = 0;
                for (let p = 0; p < npts; p++) {
                    px += v.zigzag();
                    py += v.zigzag();
                    d += `${p === 0 ? "M" : p === 1 ? "L" : " "}${km(px + ox)} ${km(py + oy)}`;
                }
                d += "Z";
            }
            dsFill.push(d);
        }
        if (dsFill.length) {
            fills.set(cid, dsFill);
        }
        const nline = v.u16();
        const pieces: LinePiece[] = [];
        for (let i = 0; i < nline; i++) {
            const fid = v.read();
            const seq = v.read();
            const flags = v.u8();
            const npts = v.read();
            const pts = new Int32Array(npts * 2);
            let px = 0;
            let py = 0;
            for (let p = 0; p < npts; p++) {
                px += v.zigzag();
                py += v.zigzag();
                pts[p * 2] = px + ox;
                pts[p * 2 + 1] = py + oy;
            }
            pieces.push({ fid, seq, flags, pts });
        }
        if (pieces.length) {
            lines.set(cid, pieces);
        }
    }
    return { fills, lines };
}

// 断片連結: 同一 fid の断片を seq (生成時の切断順) で並べて1本へ戻す。
// 端点一致による連結は量子化衝突 (別々の境界交点が同一座標へ丸まる) で誤ペアリング
// し得るため使わない。seq が連続し端点が一致する区間だけを繋ぎ、欠け (未読タイル) が
// あればそこで開いた線として分ける (端点は常に画面外)。
function stitch(pieces: LinePiece[]): { pts: number[]; closed: boolean }[] {
    const chains: { pts: number[]; closed: boolean }[] = [];
    const frags = pieces.filter((p) => p.flags !== 2).sort((a, b) => a.seq - b.seq);
    for (const p of pieces) {
        if (p.flags === 2) {
            chains.push({ pts: Array.from(p.pts), closed: true });
        }
    }
    const isRing = frags.some((p) => p.flags === 1);
    const runs: { pts: number[]; first: number; last: number }[] = [];
    for (const p of frags) {
        const prev = runs[runs.length - 1];
        if (
            prev &&
            p.seq === prev.last + 1 &&
            prev.pts[prev.pts.length - 2] === p.pts[0] &&
            prev.pts[prev.pts.length - 1] === p.pts[1]
        ) {
            for (let n = 2; n < p.pts.length; n++) {
                prev.pts.push(p.pts[n]);
            }
            prev.last = p.seq;
        } else {
            runs.push({ pts: Array.from(p.pts), first: p.seq, last: p.seq });
        }
    }
    // 円環の巻き戻し: seq 0 から始まる run の先頭へ末尾 run が繋がるなら結合
    if (isRing && runs.length > 1) {
        const first = runs[0];
        const last = runs[runs.length - 1];
        if (
            first.first === 0 &&
            last.pts[last.pts.length - 2] === first.pts[0] &&
            last.pts[last.pts.length - 1] === first.pts[1]
        ) {
            for (let n = 2; n < first.pts.length; n++) {
                last.pts.push(first.pts[n]);
            }
            runs.shift();
        }
    }
    for (const run of runs) {
        const pts = run.pts;
        const closed =
            isRing &&
            pts.length >= 8 &&
            pts[0] === pts[pts.length - 2] &&
            pts[1] === pts[pts.length - 1];
        chains.push({ pts, closed });
    }
    return chains;
}

function chainToD(chain: { pts: number[]; closed: boolean }): string {
    const pts = chain.pts;
    // 閉リング: 末尾が先頭の重複ならそれだけを省く。flags=2 断片は生成側で重複を
    // 省いて格納しているため、無条件に末尾を落とすと実頂点が欠けて弦になる (バグ修正)。
    const dup =
        chain.closed &&
        pts.length >= 4 &&
        pts[0] === pts[pts.length - 2] &&
        pts[1] === pts[pts.length - 1];
    const last = dup ? pts.length - 2 : pts.length;
    let d = "";
    for (let i = 0; i < last; i += 2) {
        d += `${i === 0 ? "M" : i === 2 ? "L" : " "}${km(pts[i])} ${km(pts[i + 1])}`;
    }
    return chain.closed ? `${d}Z` : d;
}

interface ViewBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

function zoomLevel(width: number): number {
    const z = Math.floor(Math.log2(SPAN / width)) + 1;
    return Math.min(Math.max(z, 0), ZMAX);
}

function neededTiles(view: ViewBox): { z: number; list: [number, number][] } {
    const z = zoomLevel(view.width);
    const n = 1 << z;
    const t = SPAN / n;
    // 回転を含む保守的 bbox (中心±半対角) + 1リング
    const cx = view.x + view.width / 2;
    const cy = view.y + view.height / 2;
    const half = Math.hypot(view.width, view.height) / 2;
    const ix0 = Math.floor((cx - half - ORIGIN) / t) - 1;
    const ix1 = Math.floor((cx + half - ORIGIN) / t) + 1;
    const iy0 = Math.floor((cy - half - ORIGIN) / t) - 1;
    const iy1 = Math.floor((cy + half - ORIGIN) / t) + 1;
    const list: [number, number][] = [];
    for (let ix = Math.max(0, ix0); ix <= Math.min(n - 1, ix1); ix++) {
        for (let iy = Math.max(0, iy0); iy <= Math.min(n - 1, iy1); iy++) {
            list.push([ix, iy]);
        }
    }
    return { z, list };
}

// 初期画角を URL パラメータ+モデル既定から求める (MapView.loadParams と同じ規則)。
// prefetch 用の先読み計算なので、外れても実描画側が正しく取り直すだけで害はない。
function initialViewFromParams(
    params: URLSearchParams,
    model: MapModel,
): ViewBox {
    const initialView = getMapMeta(model).initialView;
    const defaultXmin = initialView.x;
    const defaultXmax = initialView.x + initialView.width;
    const xmin = readNumber(params, "vw_lonmin", defaultXmin);
    const xmax = readNumber(params, "vw_lonmax", defaultXmax);
    const validWidth = xmax > xmin && xmax - xmin <= 100000;
    const x = validWidth ? xmin : defaultXmin;
    const width = (validWidth ? xmax : defaultXmax) - x;
    return {
        x,
        y: readNumber(params, "vw_latmin", initialView.y),
        width,
        height: (width / 16) * 9,
    };
}

export class BasemapTiles {
    static isEnabled(params: URLSearchParams): boolean {
        // 既定はタイル下地 (2026-07-22 発注者確認済み)。旧 SVG は bm=svg で明示
        // (ロールバック用に残置)。bm=tiles も従来どおり受ける。
        return params.get("bm") !== "svg";
    }

    // ===== 共有ネットワーク資源 (static) =====
    // 初回描画の高速化のため、App.fetch() 段階の prefetch() と App.init() 経由の
    // activate() のどちらが先でも同じ取得を共有する。
    private static archive: PMTiles | null = null;
    private static metadataCheck: Promise<void> | null = null;
    private static clipareaText: Promise<string> | null = null;
    private static readonly cache = new Map<string, Promise<DecodedTile>>();
    private static readonly cacheOrder: string[] = [];

    // アーカイブ header・metadata 照合・cliparea の取得を開始する (多重呼び出し可)。
    // metadata 照合 (fail-fast) はここでは await せず、onViewChanged() が DOM 更新前に
    // 確認する — header 16KB → metadata → タイルの直列往復を避けるため。
    private static startNetwork(): void {
        if (BasemapTiles.archive) {
            return;
        }
        const archive = new PMTiles(dataPath(ARCHIVE));
        BasemapTiles.archive = archive;
        BasemapTiles.metadataCheck = (async () => {
            const meta = (await archive.getMetadata()) as {
                origin?: number[];
                span_km?: number;
                units?: number;
                zmax?: number;
            };
            if (
                meta.origin?.[0] !== ORIGIN ||
                meta.span_km !== SPAN ||
                meta.units !== UNITS ||
                meta.zmax !== ZMAX
            ) {
                throw new Error(
                    "basemap metadata mismatch (archive と web の格子定数が不一致)",
                );
            }
        })();
        // 検証は onViewChanged() 側で await して再 throw する (ここでは未処理拒否だけ抑止)
        BasemapTiles.metadataCheck.catch(() => {});
        BasemapTiles.clipareaText = fetch(resourcePath("cliparea_jp.svg"))
            .then((res) => (res.ok ? res.text() : ""))
            .catch(() => "");
    }

    // 初回描画の前倒し: App.fetch() (latest.json 等と並行) から呼び、アーカイブ header・
    // metadata・cliparea に加えて初期画角ぶんのタイル取得まで開始しておく。
    static prefetch(params: URLSearchParams, model: MapModel): void {
        if (!BasemapTiles.isEnabled(params)) {
            return;
        }
        BasemapTiles.startNetwork();
        const { z, list } = neededTiles(initialViewFromParams(params, model));
        for (const [ix, iy] of list) {
            BasemapTiles.getTile(z, ix, iy).catch(() => {}); // 実描画側で再取得される
        }
    }

    private static getTile(
        z: number,
        ix: number,
        iy: number,
    ): Promise<DecodedTile> {
        const k = `${z}/${ix}/${iy}`;
        let p = BasemapTiles.cache.get(k);
        if (!p) {
            p = (async () => {
                const res = await BasemapTiles.archive!.getZxy(z, ix, iy);
                if (!res?.data?.byteLength) {
                    return { fills: new Map(), lines: new Map() };
                }
                const raw = await inflate(new Uint8Array(res.data));
                return decodeTile(raw, z, ix, iy);
            })();
            BasemapTiles.cache.set(k, p);
            BasemapTiles.cacheOrder.push(k);
            while (BasemapTiles.cacheOrder.length > 256) {
                BasemapTiles.cache.delete(BasemapTiles.cacheOrder.shift()!);
            }
        }
        return p;
    }

    private container: SVGSVGElement | null = null;
    private readonly fillGroups = new Map<number, SVGGElement>();
    private readonly lineGroups = new Map<number, SVGGElement>();
    private sequence = 0;
    private lastKey = "";

    constructor(private readonly mapView: MapView) {}

    // 合成下地コンテナを #svg_defs に組み立てる (旧 SVG の置き換え)。
    // metadata 照合はタイル取得と並列 (onViewChanged() が DOM 更新前に確認する)。
    async activate(): Promise<void> {
        if (this.container) {
            return;
        }
        BasemapTiles.startNetwork();
        document.querySelector("#svg_defs > [data-gpv-basemap]")?.remove();
        const root = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
        root.setAttribute("id", "svg");
        root.setAttribute("data-gpv-basemap", "tiles");
        const order: [string, number | null, boolean][] = [
            // [id, cid, 塗りあり]
            ["japan_pref-b", 2, false],
            ["japan_hokkaido-b", 3, false],
            ["japan_coast", 1, true],
            ["foreign_coast", 4, true],
            ["foreign_border", 5, false],
            ["lake", 6, true],
            ["foreign_lake", null, false],
            ["city", null, false],
        ];
        for (const [id, cid, hasFill] of order) {
            const g = document.createElementNS(SVG_NS, "g");
            g.setAttribute("id", id);
            if (cid !== null && hasFill) {
                const gf = document.createElementNS(SVG_NS, "g");
                gf.setAttribute("stroke", "none"); // 塗り: タイル境界辺を線に見せない
                gf.setAttribute("fill-rule", "evenodd");
                const gl = document.createElementNS(SVG_NS, "g");
                gl.setAttribute("fill", "none"); // 線: 連結パス (閉じても塗らない)
                g.append(gf, gl);
                this.fillGroups.set(cid, gf);
                this.lineGroups.set(cid, gl);
            } else if (cid !== null) {
                g.setAttribute("fill", "none");
                this.lineGroups.set(cid, g);
            }
            root.appendChild(g);
        }
        root.appendChild(this.makeGraticule());
        document.querySelector("#svg_defs")!.appendChild(root);
        // 等値線クリップ (旧 GPV.svg 内定義) を静的断片から注入
        // (fetch は startNetwork() で開始済み — 等値線が clip なしで一瞬描かれないよう
        //  従来どおり activate 完了前に注入を終える)
        if (!document.querySelector("#cliparea_GSM")) {
            const frag = await BasemapTiles.clipareaText!;
            if (frag) {
                const doc = new DOMParser().parseFromString(
                    `<svg xmlns="${SVG_NS}">${frag}</svg>`,
                    "image/svg+xml",
                );
                for (const clip of Array.from(
                    doc.documentElement.children,
                )) {
                    document
                        .querySelector("#svg_defs")!
                        .appendChild(document.importNode(clip, true));
                }
            }
        }
        this.container = root;
    }

    // 緯経線: ポーラー座標では緯線=同心円、経線=放射線 (数式生成、タイル不要)
    private makeGraticule(): SVGGElement {
        const g = document.createElementNS(SVG_NS, "g");
        g.setAttribute("id", "lonlat");
        g.setAttribute("fill", "none");
        const RE = 6371.2;
        const radius = (lat: number) =>
            2 * RE * Math.tan(((90 - lat) * Math.PI) / 360);
        const rmax = Math.hypot(SPAN / 2, SPAN / 2) * 1.02;
        for (let lat = 80; lat >= -40; lat -= 10) {
            const r = radius(lat);
            if (r > rmax) {
                continue;
            }
            const c = document.createElementNS(SVG_NS, "circle");
            c.setAttribute("cx", "0");
            c.setAttribute("cy", "0");
            c.setAttribute("r", String(Math.round(r * 100) / 100));
            g.appendChild(c);
        }
        // GPV-NH と同じ北極域の扱い: 経線は 0/90/180/270°E だけ極 (90°) まで描いて
        // 十字にし、その他は 80° 止まり (発注者指示 2026-07-22)。
        const r80 = radius(80);
        const r1 = rmax;
        for (let lon = 0; lon < 360; lon += 10) {
            const th = ((lon - 140) * Math.PI) / 180;
            const r0 = lon % 90 === 0 ? 0 : r80;
            const line = document.createElementNS(SVG_NS, "line");
            line.setAttribute("x1", String(Math.round(r0 * Math.sin(th) * 100) / 100));
            line.setAttribute("y1", String(Math.round(r0 * Math.cos(th) * 100) / 100));
            line.setAttribute("x2", String(Math.round(r1 * Math.sin(th) * 100) / 100));
            line.setAttribute("y2", String(Math.round(r1 * Math.cos(th) * 100) / 100));
            g.appendChild(line);
        }
        return g;
    }

    async onViewChanged(): Promise<void> {
        if (!this.container) {
            return;
        }
        const seq = ++this.sequence;
        const { z, list } = neededTiles(this.mapView.getViewBox());
        const viewKey = `${z}:${list.map((t) => t.join("/")).join(",")}`;
        if (viewKey === this.lastKey) {
            return;
        }
        // metadata 照合 (fail-fast) は初回タイル群と並列に走らせているため、
        // DOM へ反映する前にここで確認する (不一致なら throw して描画しない)
        const [tiles] = await Promise.all([
            Promise.all(list.map(([ix, iy]) => BasemapTiles.getTile(z, ix, iy))),
            BasemapTiles.metadataCheck,
        ]);
        if (seq !== this.sequence) {
            return;
        }
        this.lastKey = viewKey;
        // 塗り: タイル毎に完結
        for (const [cid, group] of this.fillGroups) {
            const frag = document.createDocumentFragment();
            for (const tile of tiles) {
                for (const d of tile.fills.get(cid) ?? []) {
                    const path = document.createElementNS(SVG_NS, "path");
                    path.setAttribute("d", d);
                    frag.appendChild(path);
                }
            }
            group.replaceChildren(frag);
        }
        // 線: fid ごとに連結して1本へ
        for (const [cid, group] of this.lineGroups) {
            const byFid = new Map<number, LinePiece[]>();
            for (const tile of tiles) {
                for (const piece of tile.lines.get(cid) ?? []) {
                    (byFid.get(piece.fid) ??
                        byFid.set(piece.fid, []).get(piece.fid)!).push(piece);
                }
            }
            const frag = document.createDocumentFragment();
            for (const pieces of byFid.values()) {
                for (const chain of stitch(pieces)) {
                    const path = document.createElementNS(SVG_NS, "path");
                    path.setAttribute("d", chainToD(chain));
                    frag.appendChild(path);
                }
            }
            group.replaceChildren(frag);
        }
        this.refreshClipCoast();
    }

    // 陸地クリップ (#clip_coast) を japan_coast の塗りから再構成
    private refreshClipCoast(): void {
        const clip = document.querySelector("#clip_coast");
        const source = this.fillGroups.get(1);
        if (!clip || !source) {
            return;
        }
        clip.replaceChildren(
            ...Array.from(source.childNodes, (n) => n.cloneNode(true)),
        );
    }
}
